import React from 'react';
import { useStatistics } from '../queries';
import { AreaChart } from '@tremor/react';
import CardContent from '@dashboard/components/CardContent';
import Card from '@dashboard/components/Card';
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import TabLink from '@dashboard/components/TabLink';
import { formatTimestamp, formatValue } from '../dates';
import { useStatisticsContext } from '../Dashboard';
import { isNumber } from 'lodash';
import ChartMeta from './ChartMeta';
import getConversionRate from '@dashboard/services/getConversionRate';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import Spinner from '@dashboard/components/Spinner';

function sumArray(array = [], metric) {
  return array.reduce(
    (accumulator, currentValue) =>
      accumulator + (isNumber(currentValue[metric]) ? currentValue[metric] : 0),
    0,
  );
}

const numberFormatter = value => Intl.NumberFormat('us').format(value).toString();

const percentageFormatter = (value = 0) =>
  `${Intl.NumberFormat('us')
    .format(value * 100)
    .toString()}%`;

export default function Views() {
  const { unit, range } = useStatisticsContext();
  const { data, isLoading: isLoadingVisits } = useStatistics('numberOfVisits');

  const { data: paymentsData, isLoading: isLoadingPayments } = useStatistics('numberOfPayments');
  const { data: subscriptionsData, isLoading: isLoadingSubscriptions } =
    useStatistics('numberOfSubscriptions');
  const { data: submissionsData, isLoading: isLoadingSubmissions } =
    useStatistics('numberOfSubmissions');

  const isLoading =
    isLoadingVisits || isLoadingPayments || isLoadingSubmissions || isLoadingSubscriptions;

  const totalVisits = data?.reduce((acc, { value }) => acc + value, 0);

  const totalNumberOfSales =
    paymentsData?.reduce((acc, { value }) => acc + value, 0) +
    subscriptionsData?.reduce((acc, { value }) => acc + value, 0) +
    submissionsData?.reduce((acc, { value }) => acc + value, 0);

  const conversionData = data?.reduce((acc, { timestamp, value }) => {
    const numberOfPayments = paymentsData?.find(
      ({ timestamp: paymentTimestamp }) => paymentTimestamp === timestamp,
    )?.value;

    const numberOfSubscriptions = subscriptionsData?.find(
      ({ timestamp: subscriptionTimestamp }) => subscriptionTimestamp === timestamp,
    )?.value;

    const numberOfSubmissions = submissionsData?.find(
      ({ timestamp: submissionTimestamp }) => submissionTimestamp === timestamp,
    )?.value;

    const conversionRate = getConversionRate(
      numberOfPayments + numberOfSubscriptions + numberOfSubmissions,
      value,
    );

    return [
      ...acc,
      {
        timestamp: formatTimestamp(timestamp, unit, range),
        'Number of views': formatValue(timestamp, value),
        'Conversion rate': formatValue(timestamp, conversionRate),
      },
    ];
  }, []);

  const maxNumberOfVisits = data && Math.max(...data?.map(({ value }) => value), 0);
  const minNumberOfVisits = data && Math.min(...data?.map(({ value }) => value), 0);

  const maxConversionRate =
    conversionData &&
    Math.max(
      ...conversionData?.map(items =>
        isNumber(items['Conversion rate']) ? items['Conversion rate'] : 0,
      ),
      0,
    ) / 100;

  const minConversionRate =
    conversionData &&
    Math.min(
      ...conversionData?.map(items =>
        isNumber(items['Conversion rate']) ? items['Conversion rate'] : 0,
      ),
      0,
    ) / 100;

  const conversionRate = getConversionRate(totalNumberOfSales, totalVisits);

  return (
    <Card variant="none">
      <TabGroup>
        <CardHeader variant="card">
          <CardHeaderTitle variant="card" className="w-full">
            <TabList className="flex gap-x-8 w-full">
              <Tab className="outline-none">
                {({ selected }) => (
                  <TabLink selected={selected}>
                    <CardHeaderTitle variant="card">
                      <div className="text-left">
                        <div className="text-base font-normal">
                          Views{' '}
                          <InfoTooltip>
                            The total number of times your pages were viewed.
                          </InfoTooltip>
                        </div>

                        <div className="text-xl mt-1.5 font-normal">
                          {numberFormatter(sumArray(conversionData, 'Number of views'))}
                        </div>
                      </div>
                    </CardHeaderTitle>
                  </TabLink>
                )}
              </Tab>

              <Tab className="outline-none">
                {({ selected }) => (
                  <TabLink selected={selected}>
                    <CardHeaderTitle variant="card">
                      <div className="text-left">
                        <div className="text-base font-normal">
                          Conversion rate{' '}
                          <InfoTooltip>
                            The percentage of visitors who completed a checkout.
                          </InfoTooltip>
                        </div>

                        <div className="text-xl mt-1.5 font-normal">
                          {percentageFormatter(conversionRate / 100)}
                        </div>
                      </div>
                    </CardHeaderTitle>
                  </TabLink>
                )}
              </Tab>
            </TabList>
          </CardHeaderTitle>
        </CardHeader>

        <CardContent variant="root">
          <TabPanels>
            <TabPanel>
              {isLoading ? (
                <div className="flex items-center justify-center h-44">
                  <Spinner />
                </div>
              ) : (
                <div className="relative pl-8">
                  {conversionData && (
                    <ChartMeta maxValue={maxNumberOfVisits} minValue={minNumberOfVisits} />
                  )}

                  <AreaChart
                    className="mt-4 h-44"
                    data={conversionData}
                    categories={['Number of views']}
                    index="timestamp"
                    colors={['amber']}
                    showAnimation
                    allowDecimals={false}
                    showLegend={false}
                    showGridLines={false}
                    showYAxis={false}
                    showXAxis
                    startEndOnly
                    showGradient
                  />
                </div>
              )}
            </TabPanel>

            <TabPanel>
              {isLoading ? (
                <div className="flex items-center justify-center h-44">
                  <Spinner />
                </div>
              ) : (
                <div className="relative pl-8">
                  {conversionData && (
                    <ChartMeta
                      maxValue={percentageFormatter(maxConversionRate)}
                      minValue={percentageFormatter(minConversionRate)}
                    />
                  )}

                  <AreaChart
                    className="mt-4 h-44"
                    data={conversionData}
                    categories={['Conversion rate']}
                    valueFormatter={value =>
                      isNumber(value) ? percentageFormatter(value / 100) : value
                    }
                    index="timestamp"
                    colors={['amber']}
                    showAnimation
                    allowDecimals={false}
                    showLegend={false}
                    showGridLines={false}
                    showYAxis={false}
                    showXAxis
                    startEndOnly
                    showGradient
                    padding={{ left: 16, right: 16 }}
                  />
                </div>
              )}
            </TabPanel>
          </TabPanels>
        </CardContent>
      </TabGroup>
    </Card>
  );
}
